import * as React from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { type ColumnDef, flexRender, getCoreRowModel, type SortingState, useReactTable } from '@tanstack/react-table'
import { differenceInCalendarDays } from 'date-fns'
import { MoveDown, MoveUp } from 'lucide-react'

import { formatLastUsedAt } from './relativeDate'

import { EnterpriseRole } from '@/api/generated'
import type { EnterpriseMemberRowResponse, GetMembersTableSortByEnum } from '@/api/teamMembersTable'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { ROLE_LABELS } from '@/constants/enterprise'
import { cn } from '@/lib/utils'

const formatRole = (role: EnterpriseRole | null | undefined): string => {
  if (!role) return '—'
  return ROLE_LABELS[role] ?? role
}

type SortDir = 'asc' | 'desc'

interface TeamMembersTableProps {
  rows: EnterpriseMemberRowResponse[]
  sortBy: GetMembersTableSortByEnum
  sortDir: SortDir
  onSortChange: (next: { sortBy: GetMembersTableSortByEnum; sortDir: SortDir }) => void
  isLoading: boolean
  isError: boolean
  emptyState: React.ReactNode
  showLastUsedAt: boolean
}

// Default direction when the user *switches* to a column. Text columns
// ascend (A→Z); numeric / date / status columns descend (most-recent /
// largest first), matching the Figma spec.
const DEFAULT_DIR: Record<GetMembersTableSortByEnum, SortDir> = {
  name: 'asc',
  email: 'asc',
  team: 'asc',
  role: 'asc',
  status: 'desc',
  totalWords: 'desc',
  wpm: 'desc',
  lastUsedAt: 'desc',
}

const formatStatus = (row: EnterpriseMemberRowResponse): string => {
  if (row.status === 'active') return 'Active'
  if (row.trialEndsAt === null) return 'Trialing'
  const daysLeft = Math.max(0, differenceInCalendarDays(new Date(row.trialEndsAt * 1000), new Date()))
  return `Trialing for ${daysLeft} ${daysLeft === 1 ? 'day' : 'days'}`
}

export const fullName = (row: EnterpriseMemberRowResponse): string | null => {
  const first = row.firstName ?? ''
  const last = row.lastName ?? ''
  const name = `${first} ${last}`.trim()
  return name || row.email
}

const memberDetailHref = (userId: string): string => `/app/usage/members/${encodeURIComponent(userId)}`

const TeamMembersTable = ({
  rows,
  sortBy,
  sortDir,
  onSortChange,
  isLoading,
  isError,
  emptyState,
  showLastUsedAt,
}: TeamMembersTableProps) => {
  const navigate = useNavigate()

  // Skip nav when the user is mid-drag selecting text in a cell, and honour
  // cmd/ctrl-click to open in a new tab. Bail when the click originated from a
  // nested interactive element so the name <Link>'s native new-tab behavior
  // isn't doubled by window.open here.
  const handleRowClick = (e: React.MouseEvent, userId: string) => {
    if ((e.target as HTMLElement).closest('a, button, [role="button"]')) return
    if (window.getSelection()?.toString()) return
    const url = memberDetailHref(userId)
    if (e.metaKey || e.ctrlKey) {
      window.open(url, '_blank', 'noopener,noreferrer')
    } else {
      navigate(url, { state: { from: 'table_row_click' } })
    }
  }

  // Bridge tanstack-table's sorting model to our manual server-side state.
  const sorting = React.useMemo<SortingState>(() => [{ id: sortBy, desc: sortDir === 'desc' }], [sortBy, sortDir])

  const handleHeaderClick = (columnId: GetMembersTableSortByEnum) => {
    if (columnId === sortBy) {
      onSortChange({ sortBy, sortDir: sortDir === 'asc' ? 'desc' : 'asc' })
    } else {
      onSortChange({ sortBy: columnId, sortDir: DEFAULT_DIR[columnId] })
    }
  }

  const columns = React.useMemo<ColumnDef<EnterpriseMemberRowResponse>[]>(
    () => [
      {
        id: 'name',
        header: 'Name',
        cell: ({ row }) => (
          // Real <Link> here gives keyboard focus + cmd/middle-click "open in
          // new tab". The whole row is also clickable (see handleRowClick) for
          // mouse users. Router state seeds viewed_from='table_row_click' on
          // same-tab navigation; cmd-click new tabs lose state and count as
          // deep links, which matches the user's intent.
          <Link
            to={memberDetailHref(row.original.userId)}
            state={{ from: 'table_row_click' }}
            className="group flex flex-col focus-visible:outline-none"
          >
            <span className="text-foreground text-[15px] font-semibold leading-5 group-hover:underline group-focus-visible:underline">
              {fullName(row.original)}
            </span>
            <span className="text-muted-foreground text-sm leading-5">{row.original.email}</span>
          </Link>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => <span className="text-muted-foreground text-sm">{formatStatus(row.original)}</span>,
      },
      {
        id: 'role',
        header: 'Role',
        cell: ({ row }) => <span className="text-muted-foreground text-sm">{formatRole(row.original.role)}</span>,
      },
      {
        id: 'team',
        header: 'Department',
        cell: ({ row }) => <span className="text-muted-foreground text-sm">{row.original.teamName ?? '—'}</span>,
      },
      {
        id: 'totalWords',
        header: '# of words',
        cell: ({ row }) => {
          // ``wordsInWindow`` is ``null`` exactly when ``words_window == "all_time"``;
          // ``0`` means the window is active but the user had no dictations in it,
          // which should render as "0" not an em-dash.
          const value = row.original.wordsInWindow ?? row.original.totalWords
          return <span className="text-muted-foreground text-sm tabular-nums">{value.toLocaleString()}</span>
        },
      },
      // WPM column temporarily hidden — backend data source is incorrect.
      // Re-enable by uncommenting once the upstream value is trustworthy.
      // {
      //   id: 'wpm',
      //   header: 'WPM',
      //   cell: ({ row }) => (
      //     <span className="text-muted-foreground text-sm tabular-nums">
      //       {row.original.wpm === null ? '—' : Math.round(row.original.wpm).toLocaleString()}
      //     </span>
      //   ),
      // },
      // Gated per-enterprise via the showLastUsedAt gate (internal admin toggle).
      ...(showLastUsedAt
        ? [
            {
              id: 'lastUsedAt',
              header: 'Last used at',
              cell: ({ row }) => (
                <span className="text-muted-foreground text-sm">{formatLastUsedAt(row.original.lastUsedAt)}</span>
              ),
            } satisfies ColumnDef<EnterpriseMemberRowResponse>,
          ]
        : []),
    ],
    [showLastUsedAt]
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    manualSorting: true,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="bg-card overflow-hidden rounded-xl border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="hover:bg-transparent">
              {headerGroup.headers.map((header) => {
                const canSort = header.column.columnDef.enableSorting !== false // tanstack's getCanSort() returns false for display columns
                const columnId = header.column.id as GetMembersTableSortByEnum
                const isActive = canSort && columnId === sortBy
                const headerContent = flexRender(header.column.columnDef.header, header.getContext())
                return (
                  <TableHead
                    key={header.id}
                    className="px-2"
                    aria-sort={isActive ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
                  >
                    {canSort ? (
                      <button
                        type="button"
                        onClick={() => handleHeaderClick(columnId)}
                        className={cn(
                          'inline-flex cursor-pointer items-center gap-1 text-xs font-semibold uppercase tracking-wider transition-colors',
                          isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        {headerContent}
                        {isActive &&
                          (sortDir === 'asc' ? (
                            <MoveUp className="size-3.5" aria-hidden="true" />
                          ) : (
                            <MoveDown className="size-3.5" aria-hidden="true" />
                          ))}
                      </button>
                    ) : (
                      <span className="text-muted-foreground inline-flex items-center text-xs font-semibold uppercase tracking-wider">
                        {headerContent}
                      </span>
                    )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isError ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length} className="text-muted-foreground py-16 text-center">
                Couldn’t load team members. Try again.
              </TableCell>
            </TableRow>
          ) : isLoading ? (
            // Lightweight inline skeleton — the table chrome stays visible so
            // headers don't shift on the first load.
            Array.from({ length: 6 }).map((_, idx) => (
              <TableRow key={`skeleton-${idx}`} className="hover:bg-transparent">
                {columns.map((col, colIdx) => (
                  <TableCell key={`skeleton-${idx}-${colIdx}`} className="px-2 py-3">
                    <div className="bg-muted h-5 w-3/4 animate-pulse rounded" />
                    {col.id === 'name' && <div className="bg-muted mt-1 h-4 w-1/2 animate-pulse rounded" />}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : rows.length === 0 ? (
            <TableRow className="hover:bg-transparent">
              <TableCell colSpan={columns.length} className="py-16 text-center">
                {emptyState}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} className="cursor-pointer" onClick={(e) => handleRowClick(e, row.original.userId)}>
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id} className="px-2 py-3">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  )
}

export default TeamMembersTable
